import { formatDistanceToNow, lightFormat } from "date-fns";
import { format, formatInTimeZone } from "date-fns-tz";
import { getCurrentInstance, isRef, nextTick, onActivated, onDeactivated, reactive, } from "vue";
import { resolvePropMeta } from "./metadata.js";
import { isNullOrWhitespace, parseJSONDate, IsVue3, getInternalInstance, } from "./util.js";
class Visitor {
    visitValue(value, meta) {
        switch (meta.type) {
            case undefined:
                throw "Missing type on value metadata";
            case "model":
            case "object":
                return this.visitObject(value, meta.typeDef);
            case "collection":
                return this.visitCollection(value, meta);
            case "enum":
                return this.visitEnumValue(value, meta);
            case "date":
                return this.visitDateValue(value, meta);
            case "file":
                return this.visitFileValue(value, meta);
            case "binary":
                return this.visitBinaryValue(value, meta);
            case "unknown":
                return this.visitUnknownValue(value, meta);
            default:
                return this.visitPrimitiveValue(value, meta);
        }
    }
}
function parseError(value, meta, details) {
    return new Error(`Encountered unparsable ${typeof value} \`${value}\` for ${meta.type} '${meta.name}'. ${details || ""}`);
}
export function parseValue(value, meta) {
    if (value == null) {
        return null;
    }
    const type = typeof value;
    switch (meta.type) {
        case "number":
        case "enum":
            if (type === "number")
                return value;
            if (type !== "string") {
                // We don't want to parse things like booleans into numbers.
                // Strings are all we should be handling.
                throw parseError(value, meta);
            }
            // Parse empty/blank as null, not zero.
            // If we parsed it as zero, clearing out an number input field
            // would default numbers to zero, even if that number is nullable.
            if (isNullOrWhitespace(value)) {
                return null;
            }
            const parsed = Number(value);
            if (isNaN(parsed)) {
                throw parseError(value, meta);
            }
            return parsed;
        case "string":
            if (type === "string")
                return value;
            if (type === "object")
                throw parseError(value, meta);
            return String(value);
        case "boolean":
            if (type === "boolean")
                return value;
            if (value === "true")
                return true;
            if (value === "false")
                return false;
            throw parseError(value, meta);
        case "collection":
            if (type !== "object" || !Array.isArray(value))
                throw parseError(value, meta);
            return value;
        case "model":
        case "object":
            if (type !== "object" || Array.isArray(value))
                throw parseError(value, meta);
            return value;
        case "date":
            let date = undefined;
            if (value instanceof Date) {
                date = value;
            }
            else if (type === "string") {
                date = parseJSONDate(value, meta.dateKind);
            }
            // isNaN is what date-fn's `isValid` calls internally,
            // but we can perform better if we just do it directly.
            if (date === undefined || isNaN(date)) {
                throw parseError(value, meta);
            }
            return date;
        case "file":
            if (value instanceof Blob)
                return value;
            throw parseError(value, meta);
        case "binary":
            if (typeof value == "string" || value instanceof Uint8Array)
                return value;
            if (meta.base64 && typeof value !== "string") {
                throw parseError(value, meta, "Value only allows base64 string representations.");
            }
            if (value instanceof ArrayBuffer)
                return new Uint8Array(value);
            throw parseError(value, meta);
    }
}
class ModelConversionVisitor extends Visitor {
    mode;
    objects = new Map();
    visitValue(value, meta) {
        // Models shouldn't contain undefined - only nulls where a value isn't present.
        if (value === undefined)
            return null;
        return super.visitValue(value, meta);
    }
    visitObject(value, meta) {
        if (value == null)
            return null;
        if (typeof value !== "object" || Array.isArray(value))
            throw parseError(value, meta);
        // Prevent infinite recursion on circular object graphs.
        if (this.objects.has(value))
            return this.objects.get(value);
        const props = meta.props;
        let target;
        if (this.mode == "convert") {
            this.objects.set(value, value);
            // If there already is metadata but it doesn't match,
            // this is bad - someone passed mismatched parameters.
            if ("$metadata" in value) {
                if (value.$metadata !== meta) {
                    throw Error(`While trying to convert object, found metadata for ${value.$metadata.name} where metadata for ${meta.name} was expected.`);
                }
                else {
                    // Performance optimization: If the object already looks like a model,
                    // and we're converting, don't descend into its child props,
                    // because they should already be correct.
                    // If they *aren't* correct, something is messed up somewhere else
                    // and is assigining non-models to props that should only be accepting models.
                    // If we don't do this optimization, we can end up with horrible call stacks
                    // when creating a new model with initial data that includes references
                    // to existing models that have an extensive amount of interconnected data.
                    return value;
                }
            }
            else {
                value.$metadata = meta;
                target = value;
            }
        }
        else if (this.mode == "map") {
            target = { $metadata: meta };
            this.objects.set(value, target);
        }
        else {
            throw `Unhandled mode ${this.mode}`;
        }
        this.objects.set(value, target);
        for (const propName in props) {
            const propVal = value[propName];
            if (!(propName in value) || propVal === undefined) {
                // All properties that are not defined need to be declared
                // so that Vue's reactivity system can discover them.
                // Null is a valid type for all model properties (or at least generated models). Undefined is not.
                target[propName] = null;
            }
            else {
                target[propName] = this.visitValue(propVal, props[propName]);
            }
        }
        return target;
    }
    visitCollection(value, meta) {
        if (value == null)
            return null;
        const parsed = parseValue(value, meta);
        const ret = this.mode == "map" ? [] : parsed;
        let j = 0;
        for (let i = 0; i < parsed.length; i++, j++) {
            const value = parsed[i];
            if (value === null &&
                (meta.itemType.type === "object" || meta.itemType.type === "model")) {
                // Skip nulls in collections, a workaround for
                // https://github.com/dotnet/runtime/issues/66187
                // https://github.com/dotnet/runtime/issues/76211
                j--;
                continue;
            }
            ret[j] = this.visitValue(value, meta.itemType);
        }
        if (ret.length != j) {
            // If we skipped one or more values above, shorten the array
            // to the correct length in case we were in "convert" mode.
            for (let i = j; i < ret.length; i++) {
                delete ret[i];
            }
            ret.length = j;
        }
        return ret;
    }
    visitDateValue(value, meta) {
        const parsed = parseValue(value, meta);
        if (parsed == null)
            return null;
        if (this.mode == "convert") {
            // Preserve object ref when converting.
            return parsed;
        }
        else if (this.mode == "map") {
            // If the parsed result is the same ref as the incoming value when mapping,
            // get a new object ref. Otherwise, return `parsed` since it must be a new Date
            // that was constructed by parseValue.
            return value === parsed ? new Date(parsed) : parsed;
        }
    }
    visitEnumValue(value, meta) {
        return parseValue(value, meta);
    }
    visitPrimitiveValue(value, meta) {
        return parseValue(value, meta);
    }
    visitFileValue(value, meta) {
        return parseValue(value, meta);
    }
    visitBinaryValue(value, meta) {
        return parseValue(value, meta);
    }
    visitUnknownValue(value, meta) {
        return value;
    }
    constructor(mode) {
        super();
        this.mode = mode;
    }
}
export function convertToModel(value, metadata) {
    if ("props" in metadata) {
        return new ModelConversionVisitor("convert").visitObject(value, metadata);
    }
    else {
        return new ModelConversionVisitor("convert").visitValue(value, metadata);
    }
}
export { convertToModel as convertValueToModel };
export function mapToModel(value, metadata) {
    if ("props" in metadata) {
        return new ModelConversionVisitor("map").visitObject(value, metadata);
    }
    else {
        return new ModelConversionVisitor("map").visitValue(value, metadata);
    }
}
export { mapToModel as mapValueToModel };
/** Visitor that maps a model to a DTO.
 * A DTO in this case is a POJO that is suitable for JSON stringification
 * for purposes of transport via an HTTP API.
 */
class MapToDtoVisitor extends Visitor {
    maxObjectDepth;
    depth = 0;
    visitObject(value, meta) {
        // If we've exceeded max depth, return undefined to prevent the
        // creation of an entry in the parent object for this object.
        if (this.depth > this.maxObjectDepth)
            return undefined;
        this.depth++;
        if (value == null)
            return null;
        const props = meta.props;
        const output = {};
        for (const propName in props) {
            const propMeta = props[propName];
            if (propMeta.dontSerialize)
                continue;
            if (propName in value) {
                const newValue = this.visitValue(value[propName], propMeta);
                if (newValue !== undefined) {
                    // Only store values that aren't undefined.
                    // We don't support any properties with undefined as their value - we shouldn't define these in the first place.
                    output[propName] = newValue;
                }
            }
            // This prop is a foreign key, and it has no value.
            // Lets check and see if the corresponding referenceNavigation prop has an object in it.
            // If it does, try and use that object's primary key as the value of our FK.
            if (output[propName] == null &&
                propMeta.role == "foreignKey" &&
                propMeta.navigationProp) {
                const objectValue = value[propMeta.navigationProp.name];
                if (objectValue) {
                    const objectValuePkValue = objectValue[propMeta.navigationProp.typeDef.keyProp.name];
                    if (objectValuePkValue != null) {
                        output[propName] = objectValuePkValue;
                    }
                }
                propMeta.principalType.keyProp.name;
            }
        }
        this.depth--;
        return output;
    }
    visitCollection(value, meta) {
        if (this.depth > this.maxObjectDepth) {
            // If we've exceeded max depth, return undefined to prevent the
            // creation of an entry in the parent object for this collection.
            return undefined;
        }
        if (value == null)
            return null;
        const parsed = parseValue(value, meta);
        const ret = [];
        for (let i = 0; i < parsed.length; i++) {
            ret[i] = this.visitValue(parsed[i], meta.itemType);
        }
        return ret;
    }
    visitDateValue(value, meta) {
        const parsed = parseValue(value, meta);
        if (parsed == null)
            return null;
        // We must exclude timezone (XXX) for DateTime, but keep it for DateTimeOffset.
        // This is needed to keep a timezone-agnostic DateTime from being translated
        // into a different timezone when we serialize it.
        if (meta.noOffset) {
            const serializeAs = meta.serializeAs || meta.dateKind;
            if (serializeAs == "date") {
                // System.Text.Json cannot deserialize into a System.DateOnly if the string contains a time part.
                return lightFormat(parsed, "yyyy-MM-dd");
            }
            if (serializeAs == "time") {
                // System.Text.Json cannot deserialize into a System.TimeOnly if the string contains a date part.
                return lightFormat(parsed, "HH:mm:ss.SSS");
            }
            return lightFormat(parsed, "yyyy-MM-dd'T'HH:mm:ss.SSS");
        }
        else {
            if (defaultTimeZone) {
                return formatInTimeZone(parsed, defaultTimeZone, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
            }
            return format(parsed, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
        }
    }
    visitFileValue(value, meta) {
        throw new Error("Files cannot be serialized to JSON.");
    }
    visitBinaryValue(value, meta) {
        const parsed = parseValue(value, meta);
        if (parsed == null)
            return null;
        if (typeof parsed == "string") {
            // Assume strings in the position of a binary are base64.
            return parsed;
        }
        throw new Error("Unexpected raw binary value in JSON context");
    }
    visitPrimitiveValue(value, meta) {
        return parseValue(value, meta);
    }
    visitEnumValue(value, meta) {
        return parseValue(value, meta);
    }
    visitUnknownValue(value, meta) {
        return value;
    }
    /**
     * Create a new DTO mapper, allowing recursive mapping up to the specified depth.
     * @param maxObjectDepth The maximum depth to serialize objects and collections at.
     * Any property with `propMeta.dontSerialize == true` will always be ignored, regardless of depth.
     */
    constructor(maxObjectDepth = 1e9) {
        // Default depth used to be 3, but this was arbitrarily limiting and would break app functionality
        // in a very mysterious way for deep hierarchies of external types.
        // An arbitrary depth limit at all is just masking application bugs caused by
        // developers creating circular dependencies in their external types.
        // Its better to let this stack overflow to make the issue apparent than it is to silently truncate
        // object graphs at arbitrary depths.
        super();
        this.maxObjectDepth = maxObjectDepth;
    }
}
export function mapToDto(value, metadata) {
    if (value == null) {
        // Values (non-objects) (which require metadata) should return the original value - either null or undefined.
        // I'm not sure how strictly necessary this is, but I didn't want to break the tests with this refactor.
        if (metadata)
            return value;
        // Objects always return null.
        return null;
    }
    if (metadata === undefined) {
        if ("$metadata" in value) {
            metadata = value.$metadata;
        }
    }
    if (!metadata) {
        throw "mapToDto requires metadata.";
    }
    if ("props" in metadata) {
        return new MapToDtoVisitor().visitObject(value, metadata);
    }
    else {
        return new MapToDtoVisitor().visitValue(value, metadata);
    }
}
export { mapToDto as mapValueToDto };
/**
 * Maps the given object to a POJO suitable for JSON serialization.
 * Will not serialize child objects or collections whose metadata includes `dontSerialize`.
 * @param object The object to map.
 * @param props A whitelisted set of props to include from the mapped object.
 * Unspecified props will be ignored. If null or undefined, all props will be included.
 */
export function mapToDtoFiltered(object, props) {
    var dto = mapToDto(object);
    if (props && dto) {
        const filteredDto = {};
        for (const field of props) {
            if (field in dto) {
                filteredDto[field] = dto[field];
            }
        }
        return filteredDto;
    }
    return dto;
}
let defaultTimeZone = null;
/** Set the timezone that will be used to display dates
 * via the `modelDisplay`/`propDisplay`/`valueDisplay` functions
 * if no other timezone is provided as a parameter,
 * and for c-datetime-picker in coalesce-vue-vuetify.
 * @param tzName An IANA tzdb timezone name, or null to clear this setting.
 */
export function setDefaultTimeZone(tzName) {
    defaultTimeZone = tzName;
}
/** Returns the current default timezone as provided to `setDefaultTimeZone`. */
export function getDefaultTimeZone() {
    return defaultTimeZone;
}
/** Visitor that maps its input to a string representation of its value, suitable for display. */
class DisplayVisitor extends Visitor {
    options;
    constructor(options) {
        super();
        this.options = options;
    }
    visitObject(value, meta) {
        if (value == null)
            return value;
        if ("displayProp" in meta && meta.displayProp) {
            return this.visitValue(value[meta.displayProp.name], meta.displayProp);
        }
        else {
            // https://stackoverflow.com/a/46908358 - stringify only first-level properties.
            // With a tweak to also not serialize the $metadata prop.
            try {
                return JSON.stringify(value, (k, v) => k === "$metadata" ? undefined : k ? "" + v : v);
            }
            catch {
                return value.toLocaleString();
            }
        }
    }
    visitCollection(value, meta) {
        if (value == null)
            return null;
        value = parseValue(value, meta);
        // Is this what we want? I think so - its the cleanest option.
        // Perhaps an prop that controls this would be best.
        if (value.length == 0)
            return "";
        const { enumeratedItemsMax = 5, enumeratedItemsSeparator = ", " } = this.options?.collection ?? {};
        if (value.length <= enumeratedItemsMax) {
            let itemMeta = meta.itemType;
            if ("manyToMany" in meta) {
                itemMeta = meta.manyToMany.farNavigationProp;
                value = value.map((i) => i[meta.manyToMany.farNavigationProp.name]);
            }
            return value
                .map((childItem) => this.visitValue(childItem, itemMeta) || "???" // TODO: what should this be for un-displayable members of a collection?
            )
                .join(enumeratedItemsSeparator);
        }
        return (value.length.toLocaleString() + " item" + (value.length == 1 ? "" : "s")); // TODO: i18n
    }
    visitEnumValue(value, meta) {
        const parsed = parseValue(value, meta);
        if (parsed == null)
            return null;
        const enumData = meta.typeDef.valueLookup[value];
        // If we can't find the enum value exactly,
        // just show the numeric value.
        // TODO: support flags enums (see metadata.ts@EnumType)
        if (!enumData)
            return parsed.toLocaleString();
        return enumData.displayName;
    }
    visitDateValue(value, meta) {
        const parsed = parseValue(value, meta);
        if (parsed == null)
            return null;
        let formatString;
        let formatOptions;
        let isLightFormatString = false;
        if (this.options) {
            let { format: formatOptionInput } = this.options;
            if (formatOptionInput) {
                if (typeof formatOptionInput == "string") {
                    formatString = formatOptionInput;
                }
                else if ("distance" in formatOptionInput) {
                    const { addSuffix = true, // Default addSuffix to true - most likely, it is desired.
                    includeSeconds = false, } = formatOptionInput;
                    return formatDistanceToNow(parsed, { addSuffix, includeSeconds });
                }
                else {
                    formatString = formatOptionInput.format;
                    formatOptions = formatOptionInput;
                }
            }
        }
        if (!formatString) {
            isLightFormatString = true;
            switch (meta.dateKind) {
                case "date":
                    formatString = "M/d/yyyy";
                    break;
                case "time":
                    formatString = "h:mm:ss aa";
                    break;
                default:
                    formatString = "M/d/yyyy h:mm:ss aa";
                    break;
            }
        }
        if (defaultTimeZone && !formatOptions?.timeZone && !meta.noOffset) {
            formatOptions = { ...formatOptions, timeZone: defaultTimeZone };
        }
        if (isLightFormatString && !formatOptions) {
            return lightFormat(parsed, formatString);
        }
        if (formatOptions?.timeZone) {
            return formatInTimeZone(parsed, formatOptions.timeZone, formatString, formatOptions);
        }
        return format(parsed, formatString, formatOptions);
    }
    visitFileValue(value, meta) {
        const parsed = parseValue(value, meta);
        if (parsed == null)
            return null;
        if (parsed instanceof File) {
            return parsed.name;
        }
        if (parsed instanceof Blob) {
            return `${parsed.type}, ${parsed.size} bytes`;
        }
        return null;
    }
    visitBinaryValue(value, meta) {
        const parsed = parseValue(value, meta);
        if (parsed == null)
            return null;
        if (typeof parsed == "string") {
            let padding = 0;
            while (parsed[parsed.length - 1 - padding] == "=")
                padding++;
            const bytes = 3 * (parsed.length / 4) - padding;
            return `${bytes.toLocaleString()} bytes`;
        }
        return `${parsed.byteLength.toLocaleString()} bytes`;
    }
    visitPrimitiveValue(value, meta) {
        const parsed = parseValue(value, meta);
        if (parsed == null)
            return null;
        if (typeof parsed == "number") {
            // Don't locale-string numbers - it puts in thousands separators.
            // This may seem like a neat idea, until you start displaying things
            // like PKs, FKs, or numbers like order numbers, invoice numbers.
            // That is to say, numbers without any useful meaning to their magnitude.
            return parsed.toString();
        }
        return parsed.toLocaleString();
    }
    visitUnknownValue(value, meta) {
        if (typeof value == "string")
            return value;
        if (typeof value == "number")
            return value.toString();
        if (typeof value == "object" && value)
            return value.toString();
        return null;
    }
}
/** Singleton instance of `GetDisplayVisitor` to be used when no options are provided. */
const defaultDisplayVisitor = new DisplayVisitor();
/**
 * Given a model instance, return a string representation of the instance suitable for display.
 * @param item The model instance to return a string representation of
 */
export function modelDisplay(item, options) {
    const modelMeta = item.$metadata;
    if (!modelMeta) {
        throw `Object has no $metadata property`;
    }
    return (options ? new DisplayVisitor(options) : defaultDisplayVisitor).visitObject(item, modelMeta);
}
/**
 * Given a model instance and a descriptor of a property on the instance,
 * return a string representation of the property suitable for display.
 * @param item An instance of the model that holds the property to be displayed
 * @param prop The property to be displayed - either the name of a property or a property metadata object.
 */
export function propDisplay(item, prop, options) {
    const propMeta = resolvePropMeta(item.$metadata, prop);
    var value = item[propMeta.name];
    return (options ? new DisplayVisitor(options) : defaultDisplayVisitor).visitValue(value, propMeta);
}
/**
 * Given a value and metadata which describes that value,
 * return a string representation of the value suitable for display.
 * @param value Any value which is valid for the metadata provided.
 * @param metadata The metadata which describes the value given.
 */
export function valueDisplay(value, metadata, options) {
    return (options ? new DisplayVisitor(options) : defaultDisplayVisitor).visitValue(value, metadata);
}
const coalescePendingQuery = Symbol();
export function bindToQueryString(vue, obj, key, { queryKey = key, mode = "replace", parse, stringify, } = {}) {
    if (isRef(obj) && typeof key == "string" && key !== "value") {
        return bindToQueryString(vue, obj, "value", { queryKey: key });
    }
    if (typeof key == "object") {
        // Handle the overload that accepts a ref directly
        //@ts-expect-error
        return bindToQueryString(vue, obj, "value", key);
    }
    let paused = false;
    onDeactivated(() => {
        paused = true;
    }, getInternalInstance(vue));
    onActivated(() => {
        paused = false;
        updateObject(vue.$route?.query[queryKey]);
    }, getInternalInstance(vue));
    const defaultValue = obj[key];
    const metadata = obj?.$metadata;
    // When the value changes, persist it to the query.
    vue.$watch(() => obj[key], (v) => {
        if (paused) {
            return;
        }
        if (!vue.$router || !vue.$route) {
            throw new Error("Could not find $router or $route on the component instance. Is vue-router installed?");
        }
        const newQuery = {
            ... //@ts-expect-error
            (vue.$router[coalescePendingQuery] || vue.$route.query),
            [queryKey]: v == null || v === ""
                ? undefined
                : stringify
                    ? stringify(v)
                    : // Use metadata to format the value if the obj has any.
                        metadata?.params?.[key]
                            ? mapToDto(v, metadata.params[key])?.toString()
                            : metadata?.props?.[key]
                                ? mapToDto(v, metadata.props[key])?.toString()
                                : // TODO: Add $metadata to DataSourceParameters/FilterParameters/ListParameters, and then support that as well.
                                    // Fallback to .tostring()
                                    String(v) ?? undefined,
        };
        // Fix https://github.com/IntelliTect/Coalesce/issues/310:
        // Vue router processes route changes delayed by a tick,
        // so if multiple querystring-bound values change in the same tick,
        // changes subsequent to the first will be reading a stale copy of `vue.$route.query`.
        // So, we store the pending new query value so that subsequent updates
        // during the same tick can read the previous update's desired end state,
        // rather than every update reading the beginning state.
        //@ts-expect-error
        vue.$router[coalescePendingQuery] = newQuery;
        //@ts-expect-error
        nextTick(() => delete vue.$router[coalescePendingQuery]);
        vue.$router[mode]({
            query: newQuery,
        }).catch((err) => { });
    });
    const updateObject = (v) => {
        // If an array somehow, grab the first value
        // (bindToQueryString doesn't support multiple same-named binds).
        v = Array.isArray(v) ? v[0] : v;
        obj[key] =
            // Use the default value if null or undefined
            v == null
                ? defaultValue
                : // Use provided parse function, if provided.
                    parse
                        ? parse(v)
                        : // Use metadata to parse the value if the obj is a DataSource.
                            metadata?.params?.[key]
                                ? mapToModel(v, metadata.params[key])
                                : metadata?.props?.[key]
                                    ? mapToModel(v, metadata.props[key])
                                    : // TODO: Add $metadata to DataSourceParameters/FilterParameters/ListParameters, and then support that as well.
                                        // Fallback to the raw value
                                        v;
    };
    // When the query changes, grab the new value.
    vue.$watch(() => vue.$route?.query[queryKey], async (v) => {
        if (IsVue3) {
            // In Vue3/VueRouter4, the component doesn't start tearing down immediately
            // upon route navigation - it takes an extra tick for that to happen.
            // If we don't guard here, then this watcher will pick up the query values of the new route
            // (which are probably all undefined for the keys we're watching for),
            // and start setting those (or `defaultValue`) onto the component instance, which will then trigger
            // the other watcher above which will put those new values back into the querystring of the new route.
            // Vue2 has a similar issue where this watcher still runs when leaving the route,
            // but by the time the other watcher above has a chance to run, the component is torn down
            // and so the bad values never make it back to the query string.
            await vue.$nextTick();
            if (paused || getInternalInstance(vue).isUnmounted) {
                return;
            }
        }
        updateObject(v);
    });
    // Fix #332: circumvent the `await nextTick` above on the initial call"
    updateObject(vue.$route?.query[queryKey]);
}
export function useBindToQueryString(obj, key, options) {
    const vue = getCurrentInstance()?.proxy;
    if (!vue)
        throw new Error("useBindToQueryString can only be used inside setup(). Consider using bindToQueryString if you're not using Vue composition API.");
    return bindToQueryString(vue, obj, key, options);
}
export function bindKeyToRouteOnCreate(vue, model, routeParamName = "id", keepQuery = false, routeName) {
    if (!vue.$router || !vue.$route) {
        throw new Error("Could not find $router or $route on the component instance. Is vue-router installed?");
    }
    routeName = routeName ?? vue.$route.name;
    vue.$watch(() => model[model.$metadata.keyProp.name], (pk, o) => {
        if (!routeName) {
            throw Error("Cannot use bindKeyToRouteOnCreate with unnamed routes.");
        }
        if (pk && !o) {
            const { href } = vue.$router.resolve({
                name: routeName ?? vue.$route.name,
                query: keepQuery ? vue.$route.query : {},
                params: {
                    ...vue.$route.params,
                    [routeParamName]: pk,
                },
            });
            // Manually replace state with the HTML5 history API
            // so that vue-router doesn't notice the route change
            // and therefore won't trigger any route transitions
            // or router-view component reconstitutions.
            window.history.replaceState(history.state, "", href);
        }
    });
}
export function useBindKeyToRouteOnCreate(model, routeParamName = "id", keepQuery = false, routeName) {
    const vue = getCurrentInstance()?.proxy;
    if (!vue)
        throw new Error("useBindToQueryString can only be used inside setup(). Consider using bindToQueryString if you're not using Vue composition API.");
    return bindKeyToRouteOnCreate(vue, model, routeParamName, keepQuery, routeName);
}
export function reactiveDataSource(source) {
    // Simple wrapper function around `reactive`. This exists because:
    // - None of the generated code imports directly from `vue`. Adding such
    //   an import for this one-off case feels weird, and also requires a lot
    //   of extra project config to get the tests to import from the right place.
    // - We don't want to just re-export `reactive` under its own name because then
    //   IDEs will get confused about where to import `reactive` from.
    return reactive(source);
}
